import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { ApiError, fetchProTierList, fetchTierList } from "../api";
import type { ProTierList, TierChampion, TierList } from "../types";
import { getChampionIconUrl, getChampionName } from "../utils/ddragon";
import { winrateColor } from "./summoner/parts";
import { label, Skeleton } from "./ui/primitives";

const ROLES = [
	["TOP", "Top"],
	["JUNGLE", "Jungle"],
	["MIDDLE", "Mid"],
	["BOTTOM", "Bot"],
	["UTILITY", "Support"]
] as const;
const TIERS = ["S", "A", "B", "C", "D"] as const;
const ROLE_KEY = "riftwatch.tierlist.role";
const MODE_KEY = "riftwatch.tierlist.mode";
type Mode = "solo" | "pro";
const pct = (c: TierChampion) => Math.round((c.wins / c.games) * 1000) / 10;

function saved(key: string, def: string): string {
	try {
		return localStorage.getItem(key) ?? def;
	} catch {
		return def;
	}
}
function save(key: string, value: string) {
	try {
		localStorage.setItem(key, value);
	} catch {
		/* not persisted */
	}
}

/** Segmented buttons, one pressed. */
function Toggle<T extends string>({ label: name, options, value, onPick }: { label: string; options: readonly (readonly [T, string])[]; value: string; onPick: (v: T) => void }) {
	return (
		<div role="group" aria-label={name} className="flex flex-wrap gap-1.5">
			{options.map(([v, text]) => (
				<button
					key={v}
					type="button"
					aria-pressed={value === v}
					onClick={() => onPick(v)}
					className={cn("h-9 cursor-pointer rounded-lg border px-3.5 text-[13px]", value === v ? "border-signal bg-raised font-bold text-signal" : "border-line text-ink-2 hover:bg-raised")}
				>
					{text}
				</button>
			))}
		</div>
	);
}

/** One tier: its letter, then the champions in rank order. */
function TierRow({ tier, champs }: { tier: string; champs: TierChampion[] }) {
	if (!champs.length) return null;
	return (
		<li className="grid grid-cols-[44px_minmax(0,1fr)] gap-3 border-t border-line py-3 first:border-t-0">
			<span
				className={cn(
					"flex h-11 w-11 items-center justify-center rounded-lg text-xl font-extrabold [font-stretch:112%]",
					tier === "S" ? "bg-signal text-on-signal" : tier === "A" ? "border-2 border-signal text-signal" : "border border-line-2 text-ink-2"
				)}
			>
				{tier}
			</span>
			<ul className="m-0 grid list-none gap-x-4 gap-y-2 p-0 sm:grid-cols-2 lg:grid-cols-3">
				{champs.map(c => (
					<li key={c.championId} className="grid grid-cols-[32px_minmax(0,1fr)_auto] items-center gap-2.5 text-[13px]">
						<img src={getChampionIconUrl(c.championId)} alt="" loading="lazy" className="h-8 w-8 rounded-[6px]" />
						<span className="truncate font-semibold">{getChampionName(c.championId)}</span>
						<span className="whitespace-nowrap text-right">
							<span className={cn("font-bold", winrateColor(pct(c)))}>{pct(c)}%</span>
							<span className="ml-2 text-xs text-ink-3">
								{c.pickRate}% pick · {c.games.toLocaleString("en-US")}g
							</span>
						</span>
					</li>
				))}
			</ul>
		</li>
	);
}

export function TierListPage() {
	const [mode, setMode] = useState(() => (saved(MODE_KEY, "solo") === "pro" ? "pro" : "solo") as Mode);
	const [role, setRole] = useState(() => saved(ROLE_KEY, "TOP"));
	const [event, setEvent] = useState<string>();
	const [solo, setSolo] = useState<TierList | null>(null);
	const [pro, setPro] = useState<ProTierList | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		setError(null);
		const fail = (e: unknown) => setError(e instanceof ApiError ? e.message : "Could not load the tier list");
		if (mode === "solo") fetchTierList().then(setSolo).catch(fail);
		else {
			setPro(null);
			fetchProTierList(event).then(setPro).catch(fail);
		}
	}, [mode, event]);

	const data = mode === "solo" ? solo : pro;
	const champs = data?.roles[role] ?? [];

	return (
		<section className="pt-8 sm:pt-12">
			<h1 className="m-0 text-[30px] font-extrabold leading-[1.05] [font-stretch:118%] sm:text-[40px]">Tier list.</h1>
			<p className="mt-3 mb-5 max-w-[640px] text-[15px] leading-normal text-ink-2 text-pretty">
				{mode === "solo"
					? `Champions by lane from Challenger and Grandmaster solo queue${solo ? ` on ${solo.region.toUpperCase()}` : ""}, ranked by win rate weighted by how many games back it up. Tiers are relative: the top tenth of each lane is S.`
					: "What the pros play: champions by lane in one tournament, ranked by how often they were picked. Tiers are relative: the most picked tenth of each lane is S. Games from LoL Esports."}
			</p>

			<div className="mb-3 flex flex-wrap items-center gap-3">
				<Toggle
					label="Source"
					options={[
						["solo", "Solo queue"],
						["pro", "Pros"]
					]}
					value={mode}
					onPick={m => {
						setMode(m);
						save(MODE_KEY, m);
					}}
				/>
				{mode === "pro" && pro && (
					<select
						aria-label="Tournament"
						value={pro.event}
						onChange={e => setEvent(e.target.value)}
						className="h-9 cursor-pointer rounded-lg border border-line bg-surface px-2.5 text-[13px] text-ink"
					>
						{pro.events.map(e => (
							<option key={e.id} value={e.id}>
								{e.name}
							</option>
						))}
					</select>
				)}
			</div>
			<div className="mb-4">
				<Toggle
					label="Lane"
					options={ROLES}
					value={role}
					onPick={r => {
						setRole(r);
						save(ROLE_KEY, r);
					}}
				/>
			</div>

			{error && (
				<div role="alert" className="max-w-[640px] rounded-[10px] border border-line-2 bg-raised px-3.5 py-3 text-sm text-ink">
					{error}
				</div>
			)}
			{!data && !error && <Skeleton className="h-[420px] rounded-[10px]" />}
			{data && (
				<div className="rounded-[10px] border border-line bg-surface p-3.5">
					<div className={cn(label, "mb-1")}>
						{data.games.toLocaleString("en-US")} games
						{"patches" in data ? data.patches.length > 0 && ` · patch ${data.patches.join(" + ")}` : ` · ${data.events.find(e => e.id === data.event)?.name}`}
					</div>
					{champs.length ? (
						<ul className="m-0 list-none p-0">
							{TIERS.map(t => (
								<TierRow key={t} tier={t} champs={champs.filter(c => c.tier === t)} />
							))}
						</ul>
					) : (
						<p className="m-0 py-2 text-sm text-ink-2">
							{mode === "solo" ? "Not enough games for this lane yet. The list fills up as high-elo games are collected, a few every minute." : "No picks in this lane for this tournament yet."}
						</p>
					)}
				</div>
			)}
		</section>
	);
}
